import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronLeft, FileSpreadsheet, FileText, Info, Loader2, Maximize2, MessageSquare, Minimize2, Pencil, Plus, Presentation, Sparkles, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CommentsRail, useDocumentComments } from '../../../../components/OfficeWorkspace/CommentsRail';
import { ANCHOR_PATTERN, OfficeEditorFrame } from '../../../../components/OfficeWorkspace/OfficeEditorFrame';
import { useResponseWorkspace } from '../../../../components/OfficeWorkspace/useOfficeWorkspace';
import { WorkspaceSideRail } from '../../../../components/OfficeWorkspace/WorkspaceSideRail';
import { useAuth } from '../../../../contexts/authContext';
import { usePortal } from '../../../../hooks/usePortal';
import { useRemoveVolumeDocument, useUpdateVolumeDocument, useVolumeDocuments } from '../../../../hooks/useVolumeDocuments';
import { colorForUserId, PresenceAvatars } from '../../../../pages/Editor/components/PresenceAvatars';
import { SaveStatus } from '../../../../pages/Editor/components/SaveStatus';
import { useEditSession } from '../../../../pages/Editor/hooks/useEditSession';
import { AISidebarShell } from '../AISidebarShell';
import { useAddVolumeDocument } from '../hooks/useAddVolumeDocument';
import { useProposalV2Context } from '../ProposalV2Context';
import { useVolumeMetadata } from './useVolumeMetadata';
import { VolumeMetadataPanel } from './VolumeMetadataPanel';
import { flattenSections, useOutlineSections } from './workingOutlineHooks';
/** Icon for document type. */ function DocTypeIcon({ type, className }) {
    if (type === 'xlsx') return /*#__PURE__*/ _jsx(FileSpreadsheet, {
        className: className
    });
    if (type === 'pptx') return /*#__PURE__*/ _jsx(Presentation, {
        className: className
    });
    return /*#__PURE__*/ _jsx(FileText, {
        className: className
    });
}
/** Infer document type from content type or filename. */ function inferDocType(contentType, filename) {
    const ct = (contentType || '').toLowerCase();
    const fn = (filename || '').toLowerCase();
    if (ct.includes('spreadsheet') || fn.endsWith('.xlsx') || fn.endsWith('.xls')) return 'xlsx';
    if (ct.includes('presentation') || fn.endsWith('.pptx') || fn.endsWith('.ppt')) return 'pptx';
    if (ct.includes('word') || fn.endsWith('.docx') || fn.endsWith('.doc')) return 'docx';
    return null;
}
/**
 * Parse content requirements for repeating document patterns.
 * Looks for descriptions like "up to 3 case studies", "4 past performance references",
 * "3 contract references, each not to exceed 4 pages", etc.
 */ function parseRepeatCount(contentReqs) {
    const repeatPatterns = [
        /(?:up\s+to\s+|no\s+more\s+than\s+|maximum\s+(?:of\s+)?)(\d+)\s+(.+?)(?:\s*,|\s*\.|$)/i,
        /(\d+)\s+(?:separate\s+)?(.+?)(?:\s+each|\s*,|\s*\.|$)/i,
        /submit\s+(\d+)\s+(.+)/i
    ];
    for (const req of contentReqs){
        const text = req.description || '';
        const lower = text.toLowerCase();
        // Only match repeating document patterns (case studies, references, etc.)
        if (!lower.includes('case stud') && !lower.includes('contract ref') && !lower.includes('past performance') && !lower.includes('reference')) continue;
        for (const pattern of repeatPatterns){
            const match = text.match(pattern);
            if (match) {
                const count = Number.parseInt(match[1], 10);
                if (count >= 2 && count <= 10) {
                    // Clean up the label
                    let label = match[2].trim();
                    // Remove trailing plural noise
                    label = label.replace(/\s*(references?|submissions?)\s*$/i, '').trim();
                    if (!label) label = req.contentType;
                    // Capitalize first letter
                    label = label.charAt(0).toUpperCase() + label.slice(1);
                    return {
                        count,
                        label
                    };
                }
            }
        }
    }
    return null;
}
/**
 * Derives the list of documents to create for a volume based on its
 * metadata: a primary document (DOCX/PPTX for prose, the volume's
 * file format) plus one document per attachment assignment that has a
 * different file type (e.g., XLSX labor categories template), plus
 * repeating documents from content requirements (e.g., 3 case studies).
 */ function deriveDocumentPlan(volume, meta) {
    const fileFormat = (volume.fileFormat || '').toLowerCase();
    const primaryType = fileFormat === 'xlsx' || volume.kind === 'workbook' ? 'xlsx' : fileFormat === 'pptx' || volume.kind === 'presentation' ? 'pptx' : 'docx';
    const namingConvention = meta.fileRequirements[0]?.namingConvention;
    const primarySourceId = volume.sourceAttachmentId || volume.sourceAttachmentRefs?.[0]?.attachmentId || '';
    const plan = [];
    const assignments = volume.attachmentAssignments ?? [];
    const sourceRefs = volume.sourceAttachmentRefs ?? [];
    // Check for repeating document patterns (e.g., "up to 3 case studies").
    const repeatPattern = parseRepeatCount(meta.contentRequirements);
    // Find a template attachment for repeating docs (the case study form, etc.)
    // Look for assignments whose note mentions the repeat label.
    const repeatTemplateRef = repeatPattern ? sourceRefs.find((r)=>{
        const fn = (r.filename || '').toLowerCase();
        const label = repeatPattern.label.toLowerCase();
        return fn.includes(label.split(' ')[0]) || fn.includes('case');
    }) : null;
    if (repeatPattern && repeatPattern.count >= 2) {
        // Create N documents from the template (e.g., 3 case studies).
        for(let i = 1; i <= repeatPattern.count; i++){
            plan.push({
                label: `${repeatPattern.label} ${i}`,
                documentType: primaryType,
                sourceAttachmentId: repeatTemplateRef?.attachmentId || primarySourceId,
                submissionFilename: ''
            });
        }
    } else {
        // Single primary document.
        plan.push({
            label: volume.displayName || 'Document',
            documentType: primaryType,
            sourceAttachmentId: primarySourceId,
            submissionFilename: namingConvention || ''
        });
    }
    // Additional documents from attachment assignments.
    // If an assignment's template is a different file type (e.g., XLSX when
    // primary is DOCX, or PPTX), create a separate document for it.
    for (const assignment of assignments){
        const ref = sourceRefs.find((r)=>{
            const num = assignment.attachmentNumber;
            return num && r.filename?.toLowerCase().includes(num);
        });
        if (!ref) continue;
        const refType = inferDocType(ref.contentType, ref.filename);
        if (!refType || refType === primaryType) continue;
        // Skip if this ref was already used as the repeat template.
        if (repeatTemplateRef && ref.attachmentId === repeatTemplateRef.attachmentId) continue;
        plan.push({
            label: assignment.attachmentLabel || ref.filename || refType.toUpperCase(),
            documentType: refType,
            sourceAttachmentId: ref.attachmentId || '',
            submissionFilename: ''
        });
    }
    return plan;
}
/**
 * Volume document workspace. Three states:
 *   1. No documents → metadata + smart create (attachment-aware)
 *   2. Documents exist → header + editor or list
 *   3. Fullscreen mode → portal to body, hides dashboard chrome
 */ export function VolumeDocumentEditor({ projectId, volume, initialSectionId }) {
    return /*#__PURE__*/ _jsx(VolumeDocumentEditorContent, {
        projectId: projectId,
        volume: volume,
        initialSectionId: initialSectionId
    }, `${projectId}:${volume.volumeId}`);
}
function VolumeDocumentEditorContent({ projectId, volume, initialSectionId }) {
    const outline = useOutlineSections(projectId, volume.volumeId);
    const initialSectionTitle = initialSectionId ? flattenSections(outline.data ?? []).find((n)=>n.sectionId === initialSectionId)?.title : undefined;
    const { data: docs, isLoading: docsLoading } = useVolumeDocuments(projectId, volume.volumeId);
    const addDoc = useAddVolumeDocument();
    const removeDoc = useRemoveVolumeDocument();
    const updateDoc = useUpdateVolumeDocument();
    const meta = useVolumeMetadata(projectId, volume);
    const { setAiContext, setSidebarOpen } = useProposalV2Context();
    const [activeDocId, setActiveDocId] = useState(null);
    const [showMetadata, setShowMetadata] = useState(false);
    const [showList, setShowList] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showAIChat, setShowAIChat] = useState(false);
    const [showPrefillModal, setShowPrefillModal] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const addMenuRef = useRef(null);
    const fullscreenPortal = usePortal('volume-editor-fullscreen');
    const batch = useRef(null);
    const completed = useRef(new Set());
    const nonce = useRef(null);
    const creating = useRef(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [batchPlan, setBatchPlan] = useState(null);
    const individualRequest = useRef(null);
    const [creationError, setCreationError] = useState(null);
    // Close add menu on outside click.
    useEffect(()=>{
        if (!showAddMenu) return;
        const handler = (e)=>{
            if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
                setShowAddMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return ()=>document.removeEventListener('mousedown', handler);
    }, [
        showAddMenu
    ]);
    // Show prefill modal when documents are first created (transition from 0 → N docs).
    const prevDocCount = useRef(0);
    useEffect(()=>{
        const currentCount = docs?.length ?? 0;
        if (prevDocCount.current === 0 && currentCount > 0) {
            setShowPrefillModal(true);
        }
        prevDocCount.current = currentCount;
    }, [
        docs?.length
    ]);
    const effectiveDocId = activeDocId ?? (docs?.length === 1 ? docs[0].shirohigeDocumentId : null);
    const activeVolumeDoc = docs?.find((d)=>d.shirohigeDocumentId === effectiveDocId);
    const documentPlan = useMemo(()=>deriveDocumentPlan(volume, meta), [
        volume,
        meta
    ]);
    const handleCreateAll = async ()=>{
        var _batch;
        if (creating.current) return;
        creating.current = true;
        (_batch = batch).current ?? (_batch.current = crypto.randomUUID());
        const plan = batchPlan ?? documentPlan.map((doc, index)=>({
                ...doc,
                projectId,
                volumeId: volume.volumeId,
                operationId: `create-all:${volume.volumeId}:${batch.current}:${index}:${doc.label}`
            }));
        setBatchPlan(plan);
        setCreationError(null);
        const remaining = plan.filter((doc)=>!completed.current.has(doc.operationId));
        setPendingCount(remaining.length);
        await Promise.all(remaining.map(async (doc)=>{
            try {
                await addDoc.mutateAsync(doc);
                completed.current.add(doc.operationId);
            } catch  {
                setCreationError('Some documents could not be created. Retry to finish creating them.');
            } finally{
                setPendingCount((count)=>count - 1);
            }
        }));
        if (plan.every((doc)=>completed.current.has(doc.operationId))) {
            batch.current = null;
            completed.current.clear();
            setBatchPlan(null);
        }
        creating.current = false;
    };
    const handleCreateDocument = async (documentType)=>{
        var _nonce, _individualRequest;
        if (creating.current) return;
        creating.current = true;
        (_nonce = nonce).current ?? (_nonce.current = crypto.randomUUID());
        (_individualRequest = individualRequest).current ?? (_individualRequest.current = {
            projectId,
            volumeId: volume.volumeId,
            label: documentType === 'xlsx' ? 'Spreadsheet' : documentType === 'pptx' ? 'Presentation' : `Document ${(docs?.length ?? 0) + 1}`,
            documentType,
            operationId: `create:${volume.volumeId}:${nonce.current}`
        });
        setShowAddMenu(false);
        setPendingCount(1);
        setCreationError(null);
        try {
            await addDoc.mutateAsync(individualRequest.current);
            individualRequest.current = null;
            nonce.current = null;
        } catch  {
            setCreationError('The document could not be created. Retry to finish creating it.');
        } finally{
            creating.current = false;
            setPendingCount(0);
        }
    };
    const handlePrefillOutline = ()=>{
        setShowPrefillModal(false);
        setAiContext({
            workspace: `volume:${volume.volumeId}`,
            label: volume.displayName,
            volumeId: volume.volumeId,
            autoMessage: `Generate an outline for "${volume.displayName}" based on the solicitation requirements, evaluation criteria, and page limits. Use the proposal writing skills to create section headings that align with the evaluation subfactors. Include all required items from the volume's submission checklist.`
        });
        setSidebarOpen(true);
    };
    if (docsLoading) {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.volume.editorRoot,
            className: "flex h-full items-center justify-center",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "h-6 w-6 animate-spin text-blue-600"
            })
        });
    }
    // No documents — show metadata + attachment-aware create plan.
    if (!docs || docs.length === 0 || batchPlan) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.volume.editorRoot,
            className: "h-full overflow-auto",
            children: [
                /*#__PURE__*/ _jsx(VolumeMetadataPanel, {
                    projectId: projectId,
                    volume: volume
                }),
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.volume.emptyState,
                    className: "px-6 py-6 border-t border-gray-200",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "max-w-2xl mx-auto",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-sm font-semibold text-gray-900 mb-3",
                                children: "Documents to create"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "space-y-2 mb-4",
                                children: (batchPlan ?? documentPlan).map((doc, i)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50/50",
                                        children: [
                                            /*#__PURE__*/ _jsx(DocTypeIcon, {
                                                type: doc.documentType,
                                                className: "w-5 h-5 text-gray-400 shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-0 flex-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "text-sm font-medium text-gray-900 truncate",
                                                        children: doc.label
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "text-xs text-gray-400",
                                                        children: [
                                                            doc.documentType.toUpperCase(),
                                                            doc.sourceAttachmentId ? ' · from template' : ' · blank'
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }, i))
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                "data-testid": Testids.volume.createDocumentButton,
                                disabled: pendingCount > 0,
                                onClick: handleCreateAll,
                                className: "w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors",
                                children: [
                                    /*#__PURE__*/ _jsx(Plus, {
                                        className: "w-4 h-4"
                                    }),
                                    pendingCount > 0 ? 'Creating…' : creationError ? 'Retry creating documents' : documentPlan.length > 1 ? `Create ${documentPlan.length} Documents` : 'Create Document'
                                ]
                            }),
                            creationError && /*#__PURE__*/ _jsx("p", {
                                role: "alert",
                                className: "mt-2 text-sm text-danger",
                                children: creationError
                            })
                        ]
                    })
                })
            ]
        });
    }
    // Documents exist — show header + editor (or list view).
    const showingEditor = !!effectiveDocId && !showList;
    const editorDocId = effectiveDocId ?? '';
    const editorContent = /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.volume.editorRoot,
        className: `flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'h-full'}`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 border-b border-gray-200 px-3 py-1.5 bg-white shrink-0",
                children: [
                    showingEditor && docs.length > 1 && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>{
                            setShowList(true);
                            setActiveDocId(null);
                        },
                        className: "p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors",
                        title: "Back to documents",
                        children: /*#__PURE__*/ _jsx(ChevronLeft, {
                            className: "w-4 h-4"
                        })
                    }),
                    showingEditor && docs.length > 1 && /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center gap-0.5",
                        children: docs.map((d)=>/*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "data-testid": Testids.volume.documentTab,
                                onClick: ()=>{
                                    setActiveDocId(d.shirohigeDocumentId);
                                    setShowList(false);
                                },
                                className: `px-2.5 py-1 text-xs rounded-md transition-colors ${d.shirohigeDocumentId === effectiveDocId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`,
                                children: d.label
                            }, d.volumeDocumentId))
                    }),
                    showingEditor && docs.length === 1 && activeVolumeDoc && /*#__PURE__*/ _jsx("span", {
                        className: "text-xs font-medium text-gray-700 truncate",
                        children: activeVolumeDoc.label
                    }),
                    initialSectionTitle && /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.volume.editorSectionTitle,
                        className: "text-xs text-muted-foreground truncate",
                        children: initialSectionTitle
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative",
                        ref: addMenuRef,
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                disabled: pendingCount > 0 || !!individualRequest.current,
                                onClick: ()=>setShowAddMenu((v)=>!v),
                                className: "inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors",
                                title: "Add another document",
                                children: [
                                    /*#__PURE__*/ _jsx(Plus, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "hidden sm:inline",
                                        children: pendingCount > 0 ? 'Creating…' : 'Add'
                                    })
                                ]
                            }),
                            showAddMenu && /*#__PURE__*/ _jsx("div", {
                                className: "absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1",
                                children: [
                                    'docx',
                                    'xlsx',
                                    'pptx'
                                ].map((docType)=>/*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        onClick: ()=>{
                                            void handleCreateDocument(docType);
                                        },
                                        className: "w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors",
                                        children: [
                                            /*#__PURE__*/ _jsx(DocTypeIcon, {
                                                type: docType,
                                                className: "w-4 h-4 text-gray-400"
                                            }),
                                            docType === 'docx' ? 'Word Document' : docType === 'xlsx' ? 'Spreadsheet' : 'Presentation'
                                        ]
                                    }, docType))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setShowMetadata((v)=>!v),
                        className: `p-1.5 rounded transition-colors ${showMetadata ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`,
                        title: "Volume info & requirements",
                        children: /*#__PURE__*/ _jsx(Info, {
                            className: "w-4 h-4"
                        })
                    }),
                    isFullscreen && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setShowAIChat((v)=>!v),
                        className: `p-1.5 rounded transition-colors ${showAIChat ? 'bg-purple-50 text-purple-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`,
                        title: "AI Assistant",
                        children: /*#__PURE__*/ _jsx(MessageSquare, {
                            className: "w-4 h-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>{
                            setIsFullscreen((v)=>!v);
                            if (isFullscreen) setShowAIChat(false);
                        },
                        className: "p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors",
                        title: isFullscreen ? 'Exit fullscreen' : 'Fullscreen',
                        children: isFullscreen ? /*#__PURE__*/ _jsx(Minimize2, {
                            className: "w-4 h-4"
                        }) : /*#__PURE__*/ _jsx(Maximize2, {
                            className: "w-4 h-4"
                        })
                    })
                ]
            }),
            creationError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                className: "px-3 py-2 text-sm text-danger",
                children: [
                    creationError,
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        disabled: pendingCount > 0,
                        onClick: ()=>void handleCreateDocument('docx'),
                        children: pendingCount > 0 ? 'Creating…' : 'Retry document creation'
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative flex-1 min-h-0 flex",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex-1 min-w-0",
                        children: [
                            showingEditor ? /*#__PURE__*/ _jsx(OnlyOfficeEmbed, {
                                shirohigeDocumentId: editorDocId,
                                container: {
                                    kind: 'volume',
                                    projectId,
                                    volumeId: volume.volumeId
                                },
                                sectionAnchor: flattenSections(outline.data ?? []).find((n)=>n.sectionId === initialSectionId)?.anchorBookmark || undefined
                            }, editorDocId) : /*#__PURE__*/ _jsx(DocumentListView, {
                                docs: docs,
                                onSelect: (docId)=>{
                                    setActiveDocId(docId);
                                    setShowList(false);
                                },
                                onRemove: (volDocId)=>removeDoc.mutate({
                                        projectId,
                                        volumeDocumentId: volDocId
                                    }),
                                onRename: (volDocId, newLabel)=>updateDoc.mutate({
                                        projectId,
                                        volumeDocumentId: volDocId,
                                        label: newLabel
                                    })
                            }),
                            showMetadata && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "absolute inset-0 bg-black/20 z-30",
                                        onClick: ()=>setShowMetadata(false)
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "absolute inset-y-0 left-0 z-40 w-full max-w-lg bg-white shadow-2xl border-r border-gray-200 overflow-auto",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "sticky top-0 z-10 flex items-center justify-between bg-white border-b border-gray-200 px-4 py-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "text-sm font-semibold text-gray-900",
                                                        children: "Volume Requirements"
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        type: "button",
                                                        onClick: ()=>setShowMetadata(false),
                                                        className: "p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors",
                                                        children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                                            className: "w-4 h-4"
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "p-4",
                                                children: /*#__PURE__*/ _jsx(VolumeMetadataPanel, {
                                                    projectId: projectId,
                                                    volume: volume
                                                })
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    isFullscreen && showAIChat && /*#__PURE__*/ _jsx("div", {
                        className: "w-96 border-l border-gray-200 bg-white flex flex-col shrink-0",
                        children: /*#__PURE__*/ _jsx(AISidebarShell, {
                            embedded: true
                        })
                    })
                ]
            })
        ]
    });
    // Fullscreen: portal to body so it covers the entire viewport.
    if (isFullscreen && fullscreenPortal) {
        return /*#__PURE__*/ createPortal(editorContent, fullscreenPortal);
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            editorContent,
            showPrefillModal && /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-50 flex items-center justify-center bg-black/30",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "bg-white rounded-xl shadow-2xl max-w-md mx-4 p-6",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3 mb-3",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center",
                                    children: /*#__PURE__*/ _jsx(Sparkles, {
                                        className: "w-5 h-5 text-purple-600"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-semibold text-gray-900",
                                            children: "Generate an outline?"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-gray-500",
                                            children: "AI can create section headings based on the solicitation"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "text-xs text-gray-600 mb-4",
                            children: [
                                "The AI will read the solicitation requirements, evaluation criteria, and volume instructions to suggest an outline for",
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: volume.displayName
                                }),
                                ". You can accept, reject, or modify the suggestions."
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: ()=>setShowPrefillModal(false),
                                    className: "px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 transition-colors",
                                    children: "Skip"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    onClick: handlePrefillOutline,
                                    className: "inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors",
                                    children: [
                                        /*#__PURE__*/ _jsx(Sparkles, {
                                            className: "w-3.5 h-3.5"
                                        }),
                                        "Generate Outline"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
/**
 * Document list with type icons, inline rename, and remove buttons.
 */ function DocumentListView({ docs, onSelect, onRemove, onRename }) {
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef(null);
    useEffect(()=>{
        if (editingId && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [
        editingId
    ]);
    const commitRename = ()=>{
        if (editingId && editValue.trim()) {
            onRename(editingId, editValue.trim());
        }
        setEditingId(null);
    };
    return /*#__PURE__*/ _jsx("div", {
        className: "h-full overflow-auto p-6",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "max-w-2xl mx-auto space-y-3",
            children: [
                /*#__PURE__*/ _jsxs("h3", {
                    className: "text-sm font-medium text-gray-500 uppercase tracking-wider",
                    children: [
                        "Documents (",
                        docs.length,
                        ")"
                    ]
                }),
                docs.map((d)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50/30 transition-colors",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>onSelect(d.shirohigeDocumentId),
                                className: "flex items-center gap-3 flex-1 min-w-0 text-left",
                                children: [
                                    /*#__PURE__*/ _jsx(DocTypeIcon, {
                                        type: d.documentType,
                                        className: "w-5 h-5 text-gray-400 shrink-0"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0 flex-1",
                                        children: [
                                            editingId === d.volumeDocumentId ? /*#__PURE__*/ _jsx("input", {
                                                ref: inputRef,
                                                value: editValue,
                                                onChange: (e)=>setEditValue(e.target.value),
                                                onBlur: commitRename,
                                                onKeyDown: (e)=>{
                                                    if (e.key === 'Enter') commitRename();
                                                    if (e.key === 'Escape') setEditingId(null);
                                                },
                                                onClick: (e)=>e.stopPropagation(),
                                                className: "w-full text-sm font-medium text-gray-900 bg-white border border-blue-300 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                                            }) : /*#__PURE__*/ _jsx("div", {
                                                className: "text-sm font-medium text-gray-900 truncate",
                                                children: d.label
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "text-xs text-gray-400 mt-0.5",
                                                children: [
                                                    d.documentType.toUpperCase(),
                                                    d.submissionFilename && ` · ${d.submissionFilename}`
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    setEditingId(d.volumeDocumentId);
                                    setEditValue(d.label);
                                },
                                className: "p-1.5 rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors",
                                title: "Rename document",
                                children: /*#__PURE__*/ _jsx(Pencil, {
                                    className: "w-3.5 h-3.5"
                                })
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>onRemove(d.volumeDocumentId),
                                className: "p-1.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors",
                                title: "Remove document",
                                children: /*#__PURE__*/ _jsx(Trash2, {
                                    className: "w-3.5 h-3.5"
                                })
                            })
                        ]
                    }, d.volumeDocumentId))
            ]
        })
    });
}
/**
 * Embeds the OnlyOffice editor for a single shirohige document.
 */ function OnlyOfficeEmbed({ shirohigeDocumentId, container, sectionAnchor }) {
    const workspace = useResponseWorkspace(container, shirohigeDocumentId);
    const caps = workspace.data?.capabilities;
    const comments = useDocumentComments(container, shirohigeDocumentId, caps?.writingOn ?? false);
    const [commentAnchor, setCommentAnchor] = useState();
    const [frameVersion, setFrameVersion] = useState('');
    const [commentsOpen, setCommentsOpen] = useState(true);
    useEffect(()=>setCommentAnchor(undefined), [
        sectionAnchor
    ]);
    const { user } = useAuth();
    const editorAnchor = commentAnchor ?? sectionAnchor;
    const { data: session, error: sessionError, refetch } = useEditSession(shirohigeDocumentId, editorAnchor && ANCHOR_PATTERN.test(editorAnchor) ? editorAnchor : '');
    const [presence, setPresence] = useState([]);
    const [saveState, setSaveState] = useState('idle');
    const [readyConfig, setReadyConfig] = useState(null);
    const config = useMemo(()=>{
        if (!session) return null;
        try {
            return JSON.parse(session.editorConfigJson);
        } catch  {
            return null;
        }
    }, [
        session
    ]);
    const selfId = useMemo(()=>config?.editorConfig?.user?.id ?? user?.id ?? '', [
        config,
        user
    ]);
    const handleUsersEvent = useCallback((ev)=>{
        const raw = ev?.data;
        if (!Array.isArray(raw)) return;
        setPresence(raw.filter((u)=>u.id && u.id !== selfId).map((u)=>({
                id: u.id,
                name: u.name,
                color: colorForUserId(u.id)
            })));
    }, [
        selfId
    ]);
    const handleStateChange = useCallback((ev)=>{
        const data = ev?.data;
        if (data === true) setSaveState('dirty');
        else if (data === false) setSaveState('saved');
    }, []);
    const handleInfo = useCallback((ev)=>{
        const mode = ev?.data?.mode;
        if (mode === 'saving') setSaveState('saving');
    }, []);
    if (sessionError) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-rose-600",
            children: "Failed to load editor session."
        });
    }
    if (!config || !session) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "h-6 w-6 animate-spin text-blue-600"
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1"
                    }),
                    /*#__PURE__*/ _jsx(PresenceAvatars, {
                        users: presence
                    }),
                    /*#__PURE__*/ _jsx(SaveStatus, {
                        state: saveState
                    }),
                    caps?.writingOn && !commentsOpen ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "link",
                        onClick: ()=>setCommentsOpen(true),
                        children: "Comments"
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-1 min-h-0",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 min-w-0",
                        children: /*#__PURE__*/ _jsx(OfficeEditorFrame, {
                            anchor: commentAnchor ?? sectionAnchor,
                            editorId: `vol-editor-${shirohigeDocumentId}`,
                            onDocumentReady: ()=>setReadyConfig(session.editorConfigJson),
                            onDocumentStateChange: handleStateChange,
                            onInfo: handleInfo,
                            onRequestUsers: handleUsersEvent,
                            ready: readyConfig === session.editorConfigJson,
                            session: session
                        }, `${commentAnchor ?? sectionAnchor ?? ''}:${frameVersion}`)
                    }),
                    caps?.writingOn && commentsOpen ? /*#__PURE__*/ _jsx("aside", {
                        className: "w-96 shrink-0 border-l border-border",
                        "aria-label": "Document comments",
                        children: /*#__PURE__*/ _jsx(WorkspaceSideRail, {
                            commentCount: comments.data?.comments.filter((c)=>!c.done).length ?? workspace.data?.openCommentCount,
                            hidden: [
                                'assist',
                                'sources',
                                'history'
                            ],
                            history: null,
                            initial: "comments",
                            onCollapse: ()=>setCommentsOpen(false),
                            panels: {
                                comments: /*#__PURE__*/ _jsx(CommentsRail, {
                                    canComment: caps.canComment,
                                    container: container,
                                    documentId: shirohigeDocumentId,
                                    sectionAnchor: commentAnchor ?? sectionAnchor,
                                    onJumpToAnchor: setCommentAnchor,
                                    onChanged: (versionId)=>{
                                        void refetch().then(()=>setFrameVersion(versionId));
                                    }
                                })
                            }
                        })
                    }) : null
                ]
            })
        ]
    });
}
